import type { EventType } from '@cocoonspace/sdk-js/v2/types/eventType'
import type { ProfileResponseSchema } from '@cocoonspace/sdk-js/v2/types/profileResponseSchema'
import { useMemo } from 'react'
import { useGetMeProfile } from '@cocoonspace/sdk-js/v2/domains/me-profile/me-profile'
import { useCopyClipboard } from '@cocoonspace/shared/hooks/use-copy-clipboard.hook'
import { Button } from '@cocoonspace/ui/components/button'
import {
	Card,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@cocoonspace/ui/components/card'
import { useLingui } from '@lingui/react/macro'
import { ChevronRightIcon, ShareIcon } from 'lucide-react'
import { LocalizedLink as Link } from '~/components/localized-link'
import { toast } from 'sonner'

import { MainContainer } from '~/components/atoms/main-container'
import { orderEventTypes } from '~/lib/order-event-types'
import { useAuth } from '~/providers/auth-provider'
import { EventTypeCard } from '../scheduling/event-types/event_type_card'
import { PoweredBy } from '../scheduling/powered-by'
import { AvatarPreview } from './general-profile-info-form/image-picker/avatar-preview'

const ShareBtn = ({ userProfileSlug }: { userProfileSlug?: string }) => {
	const { t } = useLingui()
	const { isAuth } = useAuth()
	const { copyToClipboard } = useCopyClipboard()
	const { data: meProfile } = useGetMeProfile()

	const isOwnProfile = meProfile?.slug === userProfileSlug

	if (!isAuth || !isOwnProfile) return null

	return (
		<Button
			variant='outline'
			size='sm'
			className='absolute top-4 right-4 z-10'
			onClick={() => {
				copyToClipboard(window.location.href).then(() => {
					toast.success(t`Le lien de votre profil a été copié`)
				})
			}}
		>
			<ShareIcon />
			{t`Partager le lien`}
		</Button>
	)
}

export const InfoCard = ({
	userPublicPage,
}: {
	userPublicPage: ProfileResponseSchema
}) => (
	<CardHeader className='flex flex-col items-center gap-6 md:flex-row'>
		<AvatarPreview
			avatarUrl={userPublicPage?.avatarUrl ?? undefined}
			className='!size-[120px]'
		/>

		<div className='flex flex-col gap-2 text-center md:text-left'>
			<CardTitle>{userPublicPage.title}</CardTitle>

			<CardDescription className='text-muted-foreground text-center whitespace-pre-line md:text-left'>
				{userPublicPage.description}
			</CardDescription>
		</div>
	</CardHeader>
)

export const EventTypes = ({
	eventTypes,
	eventTypeBasePath,
	profileSlugHref,
}: {
	eventTypes: EventType[]
	eventTypeBasePath?: string
	profileSlugHref?: string
}) => (
	<>
		{eventTypes?.map((eventType) => (
			<Link
				key={eventType.id ?? eventType.title}
				href={
					eventTypeBasePath
						? `${eventTypeBasePath}/${eventType.id}`
						: profileSlugHref
						? `/${profileSlugHref}/${eventType.id}`
						: `/scheduling/${eventType.id}`
				}
				className='group'
			>
				<EventTypeCard
					eventType={eventType}
					append={() => <ChevronRightIcon className='mr-4' />}
					className='group-hover:!bg-primary/5 rounded-none transition-colors duration-400'
				/>
			</Link>
		))}
	</>
)

export const UserPublicPageContent = ({
	userProfile,
	eventTypeBasePath,
	profileSlugHref,
}: {
	userProfile: ProfileResponseSchema
	eventTypeBasePath?: string
	profileSlugHref?: string
}) => {
	const orderedEventTypes = useMemo(() => {
		const ids = (userProfile as unknown as { eventTypeIds?: string[] })
			.eventTypeIds
		return orderEventTypes(userProfile.eventTypes, ids)
	}, [userProfile])

	return (
		<Card className='flex w-full flex-col divide-y overflow-hidden md:min-w-[500px]'>
			<InfoCard userPublicPage={userProfile} />

			<EventTypes
				eventTypes={orderedEventTypes}
				eventTypeBasePath={eventTypeBasePath}
				profileSlugHref={profileSlugHref}
			/>
		</Card>
	)
}

export const UserPublicPage = ({
	userProfile,
	embedded = false,
	eventTypeBasePath,
	profileSlugHref,
}: {
	userProfile: ProfileResponseSchema
	embedded?: boolean
	eventTypeBasePath?: string
	profileSlugHref?: string
}) => {
	return (
		<MainContainer className='flex min-h-screen max-w-3xl flex-col items-center justify-center gap-4 py-12'>
			{!embedded && <ShareBtn userProfileSlug={userProfile.slug ?? undefined} />}

			<UserPublicPageContent
				userProfile={userProfile}
				eventTypeBasePath={eventTypeBasePath}
				profileSlugHref={profileSlugHref}
			/>

			<PoweredBy />
		</MainContainer>
	)
}
